// The PR-by-id half of the repo workspace's data layer: one pull request, its
// AI review runs, and the approve/skip decision on it. Split out of
// `repo-detail.ts` (dashboard 150-LOC cap) because these address ONE PR by its
// own id (`/v1/prs/:id`) rather than a repo by `full_name` — a different reason
// to change, and its own cache namespace. `repo-detail` re-exports them, so
// existing consumers import from either.

import { ReviewRunDeliveryWireSchema, TaskStatusSchema } from '@developerz/domain';
import { createQuery } from '@tanstack/solid-query';
import { z } from 'zod';
import { t } from '../i18n';
import { activeAccountId } from './active-account';
import { apiFetch } from './api';
import { type ApiCall, sendApiCall } from './api-call';
import { ApiError, UserFacingError } from './api-errors';
import { createLiveQuery } from './live/live-query';
import type { RepoPrRow, ReviewRunRow } from './repo-detail-types';
import { SESSION_TOKEN } from './session';

export const PR_QUERY_KEY = 'pr-detail';

/**
 * One pull request (#1036). `GET /v1/prs/:id` answers the SAME projection as a
 * list row (`PullRequestResource` serves both), so the pending drawer renders server
 * truth instead of whatever the list last fetched — and re-reads it after a
 * decision. Keyed by PR id; idle until one is selected (`null` disables it).
 * LIVE on the PR's ask: the `approvals` topic is keyed by PR id, so a change to
 * this PR's pending ask (a push, a CI flip, a decision elsewhere) re-reads it.
 */
export function usePr(prId: () => string | null, token: string | null = SESSION_TOKEN) {
  return createLiveQuery<RepoPrRow>(() => {
    const id = prId();
    return {
      queryKey: [PR_QUERY_KEY, id, token, activeAccountId()],
      enabled: token !== null && id !== null,
      live: id === null ? [] : [{ entity: 'approval', id }],
      queryFn: () => apiFetch<RepoPrRow>(`/v1/prs/${encodeURIComponent(id ?? '')}`, token),
    };
  }, token).query;
}

/**
 * `GET /v1/prs/:id/review-runs`, PARSED at the seam (plan 102 slice 05 added `delivery`,
 * the run's outbox: a body missing it is the screen's error naming the field, never a pill
 * that silently reads "Reviewed" for a review still posting).
 */
const reviewRunsSchema = z.object({
  data: z.array(
    z.object({
      task_id: z.string(),
      run_id: z.string().nullable(),
      status: TaskStatusSchema,
      pr_number: z.number(),
      head_sha: z.string(),
      base_ref: z.string(),
      incremental: z.boolean(),
      created_at: z.string(),
      updated_at: z.string(),
      delivery: ReviewRunDeliveryWireSchema.nullable(),
    }),
  ),
});

/**
 * The AI code-review runs for one PR (slice 06, #64), newest-first. Read-only —
 * each run links to its `/fleet/runs/:run_id` event timeline. Keyed by PR id;
 * idle until a PR is selected (empty `prId` disables the fetch).
 */
export function useReviewRuns(prId: () => string, token: string | null = SESSION_TOKEN) {
  return createQuery<readonly ReviewRunRow[]>(() => ({
    queryKey: [PR_QUERY_KEY, 'review-runs', prId(), token, activeAccountId()],
    enabled: token !== null && prId() !== '',
    queryFn: async () =>
      (
        await apiFetch(`/v1/prs/${encodeURIComponent(prId())}/review-runs`, token, undefined, {
          schema: reviewRunsSchema,
        })
      ).data,
  }));
}

/** Which way a parked `ask_before_acting` decision goes. */
export type PendingDecision = 'approve' | 'skip';

/**
 * A decision the API refused because of the HEAD it was about (plan 101 M2),
 * with the sentence to show as its message — so `createMutation`'s
 * `errorReason` renders it like any other refusal. Branches on the problem's
 * machine half (`reason`), never on its prose:
 *
 *   - `head_moved` — commits landed after the row was rendered. Nothing was
 *     written; the caller re-reads and the human decides the NEW head. Never
 *     retried here on `current_head_sha`: that would approve a head nobody saw,
 *     which is the exact merge this contract exists to refuse.
 *   - `head_unknown` — no head projected yet, nothing to decide OF. The rows
 *     already hold their buttons on a null `head_sha`; this is the race where
 *     the row had one and the server has none.
 */
export class HeadRefusal extends UserFacingError {
  constructor(
    readonly kind: 'head_moved' | 'head_unknown',
    readonly currentHeadSha: string | null,
  ) {
    super(kind === 'head_moved' ? t('approvals.head_moved') : t('approvals.head_unknown'));
    this.name = 'HeadRefusal';
  }
}

/** The 409 `head_moved` / `head_unknown` refusal as a {@link HeadRefusal}; anything else, itself. */
export function headRefusal(error: unknown): unknown {
  if (!(error instanceof ApiError) || error.status !== 409) return error;
  const reason = error.extensions?.['reason'];
  if (reason !== 'head_moved' && reason !== 'head_unknown') return error;
  const current = error.extensions?.['current_head_sha'];
  return new HeadRefusal(reason, typeof current === 'string' ? current : null);
}

/**
 * Approve or skip a PR's pending (ask_before_acting) action, OF `headSha` —
 * the head the row the human decided on was showing. A PR with no known head
 * cannot be decided, so the type takes a string and the rows hold their
 * buttons on a null one.
 */
export async function decidePending(
  prId: string,
  decision: PendingDecision,
  headSha: string,
  token: string | null = SESSION_TOKEN,
): Promise<void> {
  try {
    await sendApiCall(prDecideCall(prId, decision, headSha), token);
  } catch (error) {
    throw headRefusal(error);
  }
}

/**
 * The decision as one value — fired by `decidePending`, shown by `<CopyAsCall/>`.
 * The two surfaces spell the same fork differently: REST forks in the PATH
 * segment, MCP in the ACTION. Both carry the head the decision is OF.
 */
export function prDecideCall(prId: string, decision: PendingDecision, headSha: string): ApiCall {
  return {
    capability: `pr.${decision}_pending`,
    method: 'POST',
    path: `/v1/prs/${encodeURIComponent(prId)}/${decision}-pending`,
    body: { head_sha: headSha },
    mcp: {
      kind: 'verb',
      verb: `pr.${decision}_pending`,
      arguments: { pr_id: prId, head_sha: headSha },
    },
  };
}
